"use client";

import React, { useEffect, useState } from "react";
import DraggableDataTable from "../../../components/DragDropTable/DragDropTable";
import { Button, Menu, MenuItem } from "@mui/material";
import { SearchInput } from "components/ui/search-input";
import { PencilIcon, X } from "lucide-react";
import highlightOrderService from "services/HighlightOrderService";
import LocationDropdown from "components/LocationDropdown/LocationDropdown";
import { toast, ToastContainer } from "react-toastify";
import CommonDialog from "components/commonDialog/CommonDialog";

const BannerManagement = ({setTabValue}) => {
  const [bannerData, setBannerData] = useState([]);

  const [selectedLocation, setSelectedLocation] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isOrderChanged, setIsOrderChanged] = useState(false);
  const [selectedHighlightId, setSelectedHighlightId] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [editingRowId, setEditingRowId] = useState(null);
  const [editingDates, setEditingDates] = useState({
    startDate: "",
    endDate: ""
  });
  const [selectedImage, setSelectedImage] = useState(null);
  
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMenuItemClick = (option) => {
    if(option == 'Coupon') {
      setTabValue('1')
    } else {
      setTabValue('2')
    }
    handleClose();
  };

  // Format date for input field (YYYY-MM-DD)
  const formatDate = (date) => {
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      timeZone: "Asia/Kolkata"
    });
  };

  
  const formatDateForInput = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-CA', {
      timeZone: 'Asia/Kolkata'
    }).split('T')[0]
  };

  // Handle duration edit
  const handleDurationEdit = (row) => {
    setEditingRowId(row.id);
    setEditingDates({
      startDate: formatDateForInput(row.startDate),
      endDate: formatDateForInput(row.endDate)
    });
  };

  // Handle duration update
  const handleDurationUpdate = async () => {
    const updateDate = {
      startDate: new Date(editingDates.startDate).toISOString(),
      endDate: new Date(editingDates.endDate).toISOString()
    }

    try {
      const res = await highlightOrderService.updateHighLight(updateDate,
        editingRowId
      );
      if (res.success) {
        toast.success("Highlight updated successfully");
        fetchHighlights(selectedLocation);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update Highlight");
    } finally {
      setSelectedHighlightId(null);
      setEditingRowId(null);
      setEditingDates({ startDate: "", endDate: "" });
    }
  };

  // Handle duration cancel
  const handleDurationCancel = () => {
    setEditingRowId(null);
    setEditingDates({ startDate: "", endDate: "" });
  };

  async function fetchHighlights(locationId) {
    setIsOrderChanged(false);
    const highlights = await highlightOrderService.getHighlights({
      location: locationId,
    });

    if (highlights && highlights?.data?.body) {
      setBannerData(highlights?.data?.body);
    }
  }

  useEffect(() => {
    if (selectedLocation) {
      fetchHighlights(selectedLocation);
    }
  }, [selectedLocation]);

  // Column definitions
  const columns = [
    {
      accessorKey: "type",
      header: "Type",
      cell: ({ row }) => (
        <div className="capitalize font-medium">{row.getValue("type")}</div>
      ),
    },
    {
      accessorKey: "title",
      header: "Title",
      cell: ({ row }) => (
        <div className="font-medium">{row.getValue("title")}</div>
      ),
    },
    {
      accessorKey: "image",
      header: "Banner",
      cell: ({ row }) => {
        const img = row.original.image;
        return (
          <div onClick={(e) => e.stopPropagation()}>
            {img && img.url ? (
              <img
                src={img.url}
                alt={img.name}
                style={{ width: 60, height: 40, objectFit: "cover", borderRadius: 4, cursor: "pointer" }}
                onClick={() => setSelectedImage(img.url)}
              />
            ) : (
              <span className="text-gray-400">No Image</span>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: "startDate",
      header: "Duration",
      cell: ({ row }) => {
        const isEditing = editingRowId === row.original.id;
        const startDate = new Date(row.getValue("startDate"));
        const endDate = new Date(row.original.endDate);

        if (isEditing) {
          return (
            <div className="flex flex-wrap items-center gap-2 sm:min-w-[500px] overflow-auto">
              <input
                type="date"
                value={editingDates.startDate}
                min={new Date().toISOString().split("T")[0]}
                onChange={(e) => setEditingDates(prev => ({ ...prev, startDate: e.target.value }))}
                className="px-2 py-1 border border-gray-300 rounded text-md w-[200px] text-[#4A4A4A]"
              />
              <input
                type="date"
                value={editingDates.endDate}
                min={editingDates.startDate || new Date().toISOString().split("T")[0]}
                onChange={(e) => setEditingDates(prev => ({ ...prev, endDate: e.target.value }))}
                className="px-2 py-1 border border-gray-300 rounded text-md w-[200px] text-[#4A4A4A]"
              />
              <Button
                size="sm"
                onClick={handleDurationUpdate}
                className="h-6 text-xs bg-blue-600 hover:bg-blue-700 text-white"
              >
                Update
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={handleDurationCancel}
                className="h-6 text-xs"
              >
                Cancel
              </Button>
            </div>
          );
        }

        return (
          <div className="text-sm">
            {formatDate(startDate)} - {formatDate(endDate)}
          </div>
        );
      },
    },
    {
      accessorKey: "duration",
      header: "Expires In",
      cell: ({ row }) => (
        <>
        {row.original.id !== editingRowId &&<div className="font-medium">{row.getValue("duration")}</div>}
        </>
      ),
    },
    {
      id: "actions",
      header: "",
      cell: ({ row }) => (
        <>
        {row.original.id !== editingRowId &&<div className="flex items-center justify-end">
          <Button
            variant="outline"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              // Check if this row is in edit mode for duration
              if (editingRowId === row.original.id) {
                // If already editing duration, don't do anything or handle differently
                return;
              }
              // If it's for general edit, handle accordingly
              handleDurationEdit(row.original);
            }}
          >
          <PencilIcon size={18}></PencilIcon>
          </Button>
          <Button
            variant="destructive"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedHighlightId(row.original.id);
            }}
          >
            <X size={18}></X>
          </Button>
        </div>}
        </>
      ),
    },
  ];

  // Handle reorder
  const handleReorder = (reorderedData) => {
    setIsOrderChanged(true);
    setBannerData(reorderedData);
  };

  // Handle row click
  const handleRowClick = (row) => {
    console.log("Banner clicked:", row);
    // Navigate to detail view or open modal
  };

  // Handle row update (if using inline editing)
  const handleRowUpdate = (originalRow, updatedData) => {
    setBannerData((prev) =>
      prev.map((item) =>
        item.id === originalRow.id ? { ...item, ...updatedData } : item
      )
    );
    console.log("Banner updated:", { originalRow, updatedData });
  };

  // Handle edit action
  const handleEdit = (banner) => {
    console.log("Edit banner:", banner);
    // Open edit modal or navigate to edit page
  };

  // Handle delete action
  const handleDelete = async () => {
    try {
      const res = await highlightOrderService.deleteHighLight(
        selectedHighlightId
      );
      if (res.success) {
        toast.success("Highlight deleted successfully");
        fetchHighlights(selectedLocation);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to delete Highlight");
    } finally {
      setSelectedHighlightId(null);
    }
  };

  const handleLocationChange = (location) => {
    if (!location) {
      setSelectedLocation(null);
      setBannerData([]);
      return;
    }
    setSelectedLocation(location.id);
  };

  const handleOrderSave = async () => {
    const newOrder = bannerData.map((data) => ({
      id: data?.id,
      order: data?.order,
    }));

    const payload = {
      newOrder,
      location: selectedLocation,
    };

    try {
      const res = await highlightOrderService.saveHighLightOrder(payload);
      if (res.success) {
        toast.success("Highlight order saved successfully");
        fetchHighlights(selectedLocation);
      }
    } catch (err) {
      toast.error(
        err.response?.data?.message || "Failed to update Highlight order"
      );
    }
  };

  return (
    <div className="mx-auto py-6">
      <div className="flex flex-col lg:flex-row lg:items-center lg:gap-2 gap-3 mb-6 lg:justify-between justify-start w-full">
        <div className="w-full lg:w-[30%]">
          <LocationDropdown
            onChange={handleLocationChange}
            className="location-dropdown"
            enabledefaultCity={true}
            placeholder="Select City to view highlights"
          />
        </div>
        <div className="flex flex-col sm:flex-row gap-2 w-full lg:w-[40%]">
          {/* <SearchInput
            onSearch={handleSearch}
            placeholder={"Title"}
            className="w-[250px]"
          /> */}
          <Button
            className="h-[40px] !bg-[#1976d2] hover:!bg-[#1976d2] rounded-[5px] font-normal text-[14px] !text-white border border-white !capitalize disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={!isOrderChanged}
            onClick={handleOrderSave}
            fullWidth={true}
            sx={{ sm: { width: 'auto' }, xs: { width: '100%' } }}
          >
            Save Order
          </Button>
          <Button
            className="h-[40px] !bg-[#e83120] hover:!bg-[#e83120] rounded-[5px] font-normal text-[14px] !text-white border border-white !capitalize disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={!isOrderChanged}
            onClick={() => fetchHighlights(selectedLocation)}
            fullWidth={true}
            sx={{ sm: { width: 'auto' }, xs: { width: '100%' } }}
          >
            Reset Order
          </Button>
          <Button
            className="h-[40px] !bg-[#ffa726] hover:!bg-[#ffa726] rounded-[5px] font-normal text-[14px] !text-white border border-white !capitalize"
            onClick={handleClick}
            aria-controls={open ? "highlight-menu" : undefined}
            aria-haspopup="true"
            aria-expanded={open ? "true" : undefined}
            fullWidth={true}
            sx={{ sm: { width: 'auto' }, xs: { width: '100%' } }}
          >
            + Add Highlight
          </Button>

          <Menu
            id="highlight-menu"
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            MenuListProps={{
              "aria-labelledby": "highlight-button",
            }}
            PaperProps={{
              style: {
                width: anchorEl?.offsetWidth || 'auto',
                boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)",
                border: "1px solid #e5e7eb",
                borderRadius: "8px",
                marginTop: "4px",
              },
            }}
          >
            <MenuItem onClick={() => handleMenuItemClick("Coupon")}>
              Coupon
            </MenuItem>
            <MenuItem onClick={() => handleMenuItemClick("Banner")}>
              Banner
            </MenuItem>
          </Menu>
        </div>
      </div>

      <DraggableDataTable
        columns={columns}
        data={bannerData}
        onReorder={handleReorder}
        onRowClick={handleRowClick}
        onRowUpdate={handleRowUpdate}
      />

      {selectedHighlightId ? (
        <CommonDialog
          open={selectedHighlightId}
          handleClose={() => setSelectedHighlightId(null)}
          toggle={setSelectedHighlightId}
          onConfirmDialog={handleDelete}
          text={"Are you sure to delete this Highlight?"}
          selectType={"delete"}
          typeOfDialog={"Highlight"}
        />
      ) : null}

{selectedImage && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" 
          onClick={() => setSelectedImage(null)}
        >
          <div 
            className="relative bg-white p-4 rounded-lg w-[800px] h-[600px] flex items-center justify-center"
            onClick={e => e.stopPropagation()}
          >
            <img
              src={selectedImage}
              alt="Preview"
              className="max-w-full max-h-full object-contain"
            />
            <button
              className="absolute top-4 right-4 bg-white rounded-full p-2 shadow-md hover:bg-gray-100"
              onClick={() => setSelectedImage(null)}
            >
              <X size={24} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default BannerManagement;
